import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";

// Bespoke, world-class landing page for XML Viewer & Editor (Android).
// Luna-inspired: light premium aesthetic, real screenshots in phone mockups,
// rigorous on-page SEO (meta + OG + JSON-LD SoftwareApplication/AggregateRating).
// Hosted at /apps/xmlvedit. Brand teal #14b8a6; assets in /xmlvedit/landing.

const PLAY = "https://play.google.com/store/apps/details?id=com.cosmovex.xmlvedit";
// Region-agnostic App Store link (no /in/ — auto-redirects to the visitor's store).
const APPSTORE = "https://apps.apple.com/app/id6761208663";
const ACCENT = "#A8720E";
const ACCENT2 = "#D9A32E";
const INK = "#26313B";
const B = "/xmlvedit/landing";
const SANS = `-apple-system, "SF Pro Display", "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;

const playClick = (e: React.MouseEvent) => {
  e.preventDefault();
  const w = window as any;
  if (w.gtag_report_conversion) w.gtag_report_conversion(PLAY);
  else window.open(PLAY, "_blank", "noopener");
};

const PlayButton: React.FC<{ small?: boolean }> = ({ small }) => (
  <a href={PLAY} onClick={playClick} aria-label="Get XML Viewer & Editor on Google Play"
    style={{
      display: "inline-flex", alignItems: "center", gap: "0.6rem",
      padding: small ? "0.75rem 1.4rem" : "1.05rem 2rem",
      background: "#E8B44C", color: "#221A06",
      borderRadius: "0.95rem", fontWeight: 700, fontSize: small ? "1rem" : "1.18rem",
      textDecoration: "none", border: `1px solid ${ACCENT2}`, boxShadow: `0 1px 2px rgba(52,44,26,0.06), 0 4px 14px rgba(52,44,26,0.05)`, fontFamily: SANS,
    }}>
    <svg width={small ? 18 : 22} height={small ? 18 : 22} viewBox="0 0 24 24" fill="currentColor">
      <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.2l2.807 1.626a1 1 0 010 1.734l-2.808 1.626L15.206 12l2.492-2.493zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z" />
    </svg>
    Get it on Google Play
  </a>
);

const AppStoreButton: React.FC<{ small?: boolean }> = ({ small }) => (
  <a href={APPSTORE} target="_blank" rel="noopener noreferrer" aria-label="Download XML Viewer & Editor on the App Store"
    style={{
      display: "inline-flex", alignItems: "center", gap: "0.6rem",
      padding: small ? "0.75rem 1.4rem" : "1.05rem 2rem",
      background: "#E8B44C", color: "#221A06",
      borderRadius: "0.95rem", fontWeight: 700, fontSize: small ? "1rem" : "1.18rem",
      textDecoration: "none", border: `1px solid ${ACCENT2}`, boxShadow: "0 1px 2px rgba(52,44,26,0.06), 0 4px 14px rgba(52,44,26,0.05)", fontFamily: SANS,
    }}>
    <svg width={small ? 18 : 22} height={small ? 18 : 22} viewBox="0 0 384 512" fill="currentColor">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
    Download on iOS
  </a>
);

const StoreRow: React.FC<{ small?: boolean; align?: string }> = ({ small, align }) => (
  <div style={{ display: "flex", gap: "0.9rem", flexWrap: "wrap", justifyContent: align === "center" ? "center" : "flex-start" }}>
    <PlayButton small={small} />
    <AppStoreButton small={small} />
  </div>
);

const Phone: React.FC<{ src: string; alt: string; w?: number }> = ({ src, alt, w = 250 }) => (
  <div style={{
    width: w, borderRadius: w * 0.12, background: "#0b0f1a", padding: w * 0.022,
    boxShadow: "0 30px 60px -18px rgba(52,44,26,0.22), 0 10px 24px rgba(52,44,26,0.10)",
  }}>
    <div style={{ borderRadius: w * 0.1, overflow: "hidden", background: "#0b0f1a", lineHeight: 0 }}>
      <img src={src} alt={alt} loading="lazy" style={{ width: "100%", height: "auto", display: "block" }} />
    </div>
  </div>
);

const FeatureCard: React.FC<{ src: string; label: string; sub: string; bg: string }> = ({ src, label, sub, bg }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
    <div style={{
      background: bg, borderRadius: "2rem", padding: "2.2rem 1.2rem 0", width: "100%",
      display: "flex", justifyContent: "center", overflow: "hidden", maxHeight: 420,
    }}>
      <Phone src={src} alt={label} w={220} />
    </div>
    <h3 style={{ fontFamily: SANS, fontWeight: 700, fontSize: "1.5rem", color: INK, margin: "1.3rem 0 0.4rem" }}>{label}</h3>
    <p style={{ fontFamily: SANS, fontSize: "1.02rem", color: "#5B6B7C", lineHeight: 1.5, margin: 0, maxWidth: 300 }}>{sub}</p>
  </div>
);

const Section: React.FC<{ title: string; sub: string; children: React.ReactNode }> = ({ title, sub, children }) => (
  <section style={{ maxWidth: 1180, margin: "0 auto", padding: "3.5rem 1.5rem" }}>
    <h2 style={{ fontFamily: SANS, fontWeight: 800, fontSize: "clamp(2rem,5vw,3.1rem)", letterSpacing: "-1.5px", color: INK, margin: 0 }}>{title}</h2>
    <p style={{ fontFamily: SANS, fontSize: "1.2rem", color: "#5B6B7C", margin: "0.8rem 0 2.6rem", maxWidth: 640, lineHeight: 1.5 }}>{sub}</p>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "2rem" }}>{children}</div>
  </section>
);

const XMLVEditLanding: React.FC = () => {
  const [showBar, setShowBar] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowBar(window.scrollY > 620);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "XML Viewer & Editor",
    operatingSystem: "Android, iOS",
    applicationCategory: "DeveloperApplication",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    aggregateRating: { "@type": "AggregateRating", ratingValue: "3.8", ratingCount: "18" },
    description: "Open, read, format and validate XML files on Android. Tree view, syntax highlighting, XPath, XSD validation and XML to JSON — free, offline, no account.",
    downloadUrl: PLAY,
    image: "https://cosmovexapps.com" + B + "/og.png",
  };

  return (
    <>
      <Helmet>
        <title>XML Viewer & Editor for Android — Open, Format & Validate XML</title>
        <meta name="description" content="Open, read, format and validate XML files on your phone. Tree view, syntax highlighting, XPath queries, XSD validation and XML to JSON — free, offline, no account." />
        <link rel="canonical" href="https://cosmovexapps.com/apps/xmlvedit" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="XML Viewer & Editor for Android" />
        <meta property="og:description" content="Open, format and validate XML on your phone — tree view, XPath, validation, XML to JSON. Free & offline." />
        <meta property="og:image" content={"https://cosmovexapps.com" + B + "/og.png"} />
        <meta property="og:url" content="https://cosmovexapps.com/apps/xmlvedit" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="keywords" content="xml editor, xml viewer, open xml file android, validate xml, xpath, format xml, xml to json, xsd validation, android xml editor" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div style={{ background: "#FBFAF6", minHeight: "100vh", fontFamily: SANS }}>
        {/* NAV */}
        <nav style={{ maxWidth: 1180, margin: "0 auto", padding: "1.4rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.7rem" }}>
            <img src={B + "/logo.png"} alt="XML Viewer & Editor logo" width={44} height={44} style={{ borderRadius: 12 }} />
            <span style={{ fontWeight: 700, fontSize: "1.15rem", color: INK }}>XML Viewer &amp; Editor</span>
          </div>
          <PlayButton small />
        </nav>

        {/* HERO */}
        <header style={{ maxWidth: 1180, margin: "0 auto", padding: "2.5rem 1.5rem 1rem", display: "grid", gridTemplateColumns: "1.1fr 0.9fr", gap: "2rem", alignItems: "center" }}
          className="xv-hero">
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", background: "#F1EEE5", color: ACCENT, fontWeight: 700, fontSize: "0.92rem", padding: "0.45rem 0.9rem", borderRadius: 999, marginBottom: "1.3rem" }}>
              ★ 3.8 · 5K+ installs · Free
            </div>
            <h1 style={{ fontWeight: 800, fontSize: "clamp(2.6rem,6vw,4.4rem)", lineHeight: 1.02, letterSpacing: "-2.5px", color: INK, margin: 0 }}>
              Open any XML file.<br />See it clearly.
            </h1>
            <p style={{ fontSize: "1.35rem", color: "#3D4B59", lineHeight: 1.45, margin: "1.4rem 0 2rem", maxWidth: 520 }}>
              A fast XML viewer &amp; editor with tree view, syntax highlighting, XPath and validation — free, offline, no account.
            </p>
            <StoreRow />
            <p style={{ color: "#8A93A2", fontSize: "0.95rem", margin: "0.9rem 0 0" }}>Free on iOS &amp; Android · Works offline · No sign-up</p>
          </div>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Phone src={B + "/hero_screen.png"} alt="XML Viewer tree view with syntax-highlighted XML" w={300} />
          </div>
        </header>

        {/* BASICS */}
        <Section title="Built to make XML readable" sub="Open a file, see the structure, and find exactly what you need — instantly.">
          <FeatureCard src={B + "/tree.png"} label="Tree view" sub="Expand, collapse and tap-to-copy any node. See structure at a glance." bg="#F1EEE5" />
          <FeatureCard src={B + "/format.png"} label="Format & highlight" sub="Color-coded syntax. Prettify or minify with one tap." bg="#F1EEE5" />
          <FeatureCard src={B + "/xpath.png"} label="XPath search" sub="Query your document with full XPath 1.0 — paths, predicates, functions." bg="#F1EEE5" />
        </Section>

        {/* POWERFUL */}
        <Section title="More than a viewer" sub="Validate, compare, convert and export — everything XML, in one offline app.">
          <FeatureCard src={B + "/stats.png"} label="Validate & analyze" sub="Catch errors, check against an XSD schema, and see full document stats." bg="#F1EEE5" />
          <FeatureCard src={B + "/tools.png"} label="13 built-in tools" sub="Convert to JSON, compare files, export to PDF or spreadsheet, and more." bg="#F1EEE5" />
          <FeatureCard src={B + "/home.png"} label="Works offline" sub="Open from a file, URL, or clipboard. Everything runs on your device." bg="#F1EEE5" />
        </Section>

        {/* WHY DIFFERENT */}
        <section style={{ background: "#F1EEE5", borderTop: "1px solid #E1DCCE", borderBottom: "1px solid #E1DCCE", margin: "2rem 0 0", padding: "4rem 1.5rem" }}>
          <div style={{ maxWidth: 880, margin: "0 auto", textAlign: "center" }}>
            <h2 style={{ fontWeight: 800, fontSize: "clamp(2rem,5vw,3rem)", letterSpacing: "-1.5px", color: "#26313B", margin: 0 }}>
              No subscription. No sign-in.<br />Your files stay on your device.
            </h2>
            <p style={{ fontSize: "1.25rem", color: "#3D4B59", lineHeight: 1.5, margin: "1.3rem auto 2.2rem", maxWidth: 620 }}>
              XML Viewer &amp; Editor handles XML the way it should: open a file, see the structure, fix what&apos;s broken, export when ready. The Pro upgrade is a one-time purchase that removes ads — that&apos;s it.
            </p>
            <div style={{ display: "flex", gap: "0.8rem", justifyContent: "center", flexWrap: "wrap", marginBottom: "2.4rem" }}>
              {["Tree view", "XPath", "XSD validation", "XML → JSON", "Offline", "One-time Pro"].map((t) => (
                <span key={t} style={{ background: "#FFFFFF", border: "1px solid #D9A32E", color: "#26313B", fontWeight: 700, fontSize: "1rem", padding: "0.6rem 1.2rem", borderRadius: 999 }}>{t}</span>
              ))}
            </div>
            <StoreRow align="center" />
          </div>
        </section>

        {/* FOOTER */}
        <footer style={{ maxWidth: 1180, margin: "0 auto", padding: "2.5rem 1.5rem 6rem", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem", color: "#8A93A2", fontSize: "0.95rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <img src={B + "/logo.png"} alt="" width={32} height={32} style={{ borderRadius: 9 }} />
            <span>© Cosmovex · XML Viewer &amp; Editor</span>
          </div>
          <div style={{ display: "flex", gap: "1.4rem" }}>
            <a href="/xmlvedit/privacy-policy.html" style={{ color: "#5B6B7C", textDecoration: "none" }}>Privacy</a>
            <a href="/xmlvedit/support.html" style={{ color: "#5B6B7C", textDecoration: "none" }}>Support</a>
            <a href="/blog/how-to-open-edit-xml-files-android-2026" style={{ color: "#5B6B7C", textDecoration: "none" }}>Guide</a>
            <a href={PLAY} onClick={playClick} style={{ color: ACCENT, fontWeight: 700, textDecoration: "none" }}>Get the app</a>
          </div>
        </footer>
      </div>

      {/* STICKY INSTALL BAR */}
      {showBar && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 1000, display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.65rem 1rem calc(0.65rem + env(safe-area-inset-bottom))", background: "rgba(251,250,246,0.96)", backdropFilter: "blur(14px)", borderTop: "1px solid #E1DCCE" }}>
          <img src={B + "/logo.png"} alt="" width={40} height={40} style={{ borderRadius: 10 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: "#26313B", fontWeight: 700, fontSize: "0.95rem" }}>XML Viewer &amp; Editor</div>
            <div style={{ color: "#5B6B7C", fontSize: "0.78rem" }}>★ 3.8 · Free on Google Play</div>
          </div>
          <PlayButton small />
        </div>
      )}

      <style>{`@media (max-width: 820px){ .xv-hero{ grid-template-columns: 1fr !important; text-align:center; } .xv-hero > div:last-child{ margin-top:1.5rem; } }`}</style>
    </>
  );
};

export default XMLVEditLanding;
